import { Account } from '@kudos/http-client'
import { useT } from '@transifex/react'
import { useEffect, useMemo, useState } from 'react'
import { useController, useFormContext, useWatch } from 'react-hook-form'
import useCanSendPoints from './useCanSendPoints'
import useRecognitionPointPresets, { type PointsChipOption } from './useRecognitionPointPresets'
import { RecognitionFormData } from './useSendRecognitionForm'

export type { PointsChipOption }

export type UsePointsSelectionOptions = {
  delegatorId?: string
}

export type UsePointsSelectionResult = {
  canSendPoints: boolean
  points: PointsChipOption[]
  allowCustomPoints: boolean
  isLoadingPresets: boolean
  amount: number
  onAmountChange: (value: number) => void
  isUsingCustomAmount: boolean
  disabledValues: string[]
  handleFixedSelection: (value: number) => void
  openCustomSelection: () => void
  recipientCount: number
  allocationPoints: number
  handleRecipientCountChange: (count: number) => void
  handleAllocationPointsChange: (points: number) => void
  sendTo: RecognitionFormData['sendTo']
  control: ReturnType<typeof useFormContext<RecognitionFormData>>['control']
  maxPerRecipient: number
  delegatorId: string | undefined
  delegatorAccounts: Account[] | undefined
  isDelegatorPointsFetched: boolean
}

const usePointsSelection = (options: UsePointsSelectionOptions = {}): UsePointsSelectionResult => {
  const { delegatorId } = options
  const t = useT()

  const { canSendPoints, delegatorAccounts, isDelegatorPointsFetched } = useCanSendPoints({
    delegatorId,
  })
  const { points, allowCustomPoints, isLoadingPresets } = useRecognitionPointPresets()

  const [disabledValues, setDisabledValues] = useState<string[]>([])
  const [isUsingCustomAmount, setIsUsingCustomAmount] = useState<boolean>(false)
  const [recipientCount, setRecipientCount] = useState<number>(0)
  const [allocationPoints, setAllocationPoints] = useState<number>(0)

  const { control, setValue, setError, trigger } = useFormContext<RecognitionFormData>()
  const {
    field: { value: amount, onChange },
  } = useController({ control, name: 'amount' })
  const sendTo = useWatch({ control, name: 'sendTo' })

  const handleRecipientCountChange = (count: number) => setRecipientCount(count)
  const handleAllocationPointsChange = (pointsValue: number) => setAllocationPoints(pointsValue)

  const maxPerRecipient = useMemo(() => {
    if (recipientCount && recipientCount > 0) {
      return Math.floor(allocationPoints / recipientCount)
    }
    return allocationPoints
  }, [allocationPoints, recipientCount])

  useEffect(() => {
    if (amount > maxPerRecipient) {
      setError('amount', {
        type: 'maxLength',
        message: t('Enter a point amount that is {maxPerRecipient} or less', { maxPerRecipient }),
      })
    } else {
      trigger('amount')
    }
  }, [amount, maxPerRecipient, setError, t, trigger])

  useEffect(() => {
    if (isLoadingPresets || points.length === 0) return
    const setPointOptions = points.map(p => parseInt(p.id, 10))
    if (!setPointOptions.includes(amount)) {
      setIsUsingCustomAmount(true)
    }
  }, [amount, isLoadingPresets, points])

  useEffect(() => {
    const calculatedDisabled = points.filter(p => Number(p.id) > maxPerRecipient).map(p => p.id)

    if (!isUsingCustomAmount && amount * recipientCount > allocationPoints) {
      const maxAllowed = points
        .filter((c: PointsChipOption) => !calculatedDisabled.includes(c.id))
        .sort((a: PointsChipOption, b: PointsChipOption) => (Number(a.id) < Number(b.id) ? -1 : 1))
        .pop()

      if (maxAllowed) {
        setValue('amount', parseInt(maxAllowed.id, 10))
      }
    }

    setDisabledValues(calculatedDisabled)
    // Intentionally only run when recipient/allocation/presets change; uses current amount/isUsingCustomAmount from closure
  }, [recipientCount, allocationPoints, points])

  const handleFixedSelection = (value: number) => {
    onChange(value)
    setIsUsingCustomAmount(false)
  }

  const openCustomSelection = () => setIsUsingCustomAmount(true)
  const onAmountChange = (value: number) => onChange(value)

  return {
    canSendPoints,
    points,
    allowCustomPoints,
    isLoadingPresets,
    amount,
    onAmountChange,
    isUsingCustomAmount,
    disabledValues,
    handleFixedSelection,
    openCustomSelection,
    recipientCount,
    allocationPoints,
    handleRecipientCountChange,
    handleAllocationPointsChange,
    sendTo,
    control,
    maxPerRecipient,
    delegatorId,
    delegatorAccounts,
    isDelegatorPointsFetched,
  }
}

export default usePointsSelection
